import { SkeletonBlock } from "@/components/podkit/loading/Skeleton";
import { ProjectFailedToLoad } from "@/components/projects/ProjectErrors";
import { useProjectPrebuilds } from "@/queries/prebuild-queries";
import { useEnvironmentClassMap } from "@/hooks/use-environment-class-entries";
import { useMemo, type FC } from "react";
import { useParams } from "react-router-dom";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/podkit/tables/Table";
import { Text } from "@/components/podkit/typography/Text";
import { Button } from "@/components/flexkit/Button";
import { IconSpinner } from "@/assets/icons/geist/IconSpinner";
import { IconCheck } from "@/assets/icons/geist/IconCheck";
import { IconClose } from "@/assets/icons/geist/IconClose";
import { cn } from "@/components/podkit/lib/cn";
import { Banner } from "@/components/flexkit/Banner";
import { Timestamp } from "@bufbuild/protobuf";
import { durationBetweenDates, formatAbsoluteDateTime, formatShortDate } from "@/format/time";
import { PrebuildPhase, PrebuildMetadata_PrebuildTrigger } from "gitpod-next-api/gitpod/v1/prebuild_pb";
import { IconStop } from "@/assets/icons/geist/IconStop";

export const ProjectPrebuildsPage: FC = () => {
    const { projectId } = useParams();
    const { data, isPending, error } = useProjectPrebuilds(projectId);
    const { environmentClasses, isLoading: isLoadingClassMap } = useEnvironmentClassMap();

    const sortedPrebuilds = useMemo(() => {
        const list = data?.prebuilds ?? [];
        return [...list].sort((a, b) => {
            const aTime = a?.metadata?.createdAt ? new Timestamp(a.metadata.createdAt).toDate().getTime() : 0;
            const bTime = b?.metadata?.createdAt ? new Timestamp(b.metadata.createdAt).toDate().getTime() : 0;
            return bTime - aTime;
        });
    }, [data?.prebuilds]);

    const runningCount = useMemo(() => {
        return (sortedPrebuilds || []).filter((pb) => {
            const phase = pb?.status?.phase;
            return (
                phase === PrebuildPhase.PENDING ||
                phase === PrebuildPhase.STARTING ||
                phase === PrebuildPhase.RUNNING ||
                phase === PrebuildPhase.STOPPING ||
                phase === PrebuildPhase.SNAPSHOTTING
            );
        }).length;
    }, [sortedPrebuilds]);

    let content: React.ReactNode | null = null;
    if (error || !projectId) {
        content = <ProjectFailedToLoad />;
    } else if (isPending || isLoadingClassMap) {
        content = <SkeletonBlock ready={false} className="h-[200px] w-full" />;
    } else if ((sortedPrebuilds?.length || 0) === 0) {
        content = (
            <div className="mt-8">
                <div
                    className="rounded-lg border border-border-light p-6 text-base text-content-secondary"
                    data-testid="prebuilds-empty-state"
                >
                    No prebuilds in the last 7 days.
                </div>
            </div>
        );
    } else {
        content = (
            <div className="flex flex-col">
                <Table>
                    <TableHeader className="[&_th]:border-b [&_th]:border-border-light [&_th]:bg-transparent">
                        <TableRow className="h-8 text-base text-content-strong">
                            <TableHead className="pl-3">Date & Time</TableHead>
                            <TableHead className="pl-3">Class</TableHead>
                            <TableHead className="pl-3">Trigger</TableHead>
                            <TableHead className="pl-3">Status & Duration</TableHead>
                            <TableHead className="w-[80px]" />
                        </TableRow>
                    </TableHeader>
                    <TableBody className="border-t border-border-light text-base" data-testid="prebuilds-table">
                        {sortedPrebuilds.map((pb) => {
                            const createdAt = pb.metadata?.createdAt
                                ? new Timestamp(pb.metadata.createdAt).toDate()
                                : undefined;
                            const completedAt = pb.status?.completionTime
                                ? new Timestamp(pb.status.completionTime).toDate()
                                : undefined;
                            const clazzEntry = pb.metadata?.environmentClassId
                                ? environmentClasses[pb.metadata.environmentClassId]
                                : undefined;

                            const triggerText = triggerToText(pb.metadata?.triggeredBy);
                            const status = statusInfo(pb.status?.phase);
                            const dur =
                                createdAt && completedAt
                                    ? durationBetweenDates(createdAt, completedAt)
                                    : createdAt && status.showSpinner
                                      ? durationBetweenDates(createdAt, new Date())
                                      : "";

                            return (
                                <TableRow key={pb.id} className="h-16 border-border-light">
                                    <TableCell>
                                        <div className="flex flex-col">
                                            <Text className="text-base">
                                                {createdAt ? formatAbsoluteDateTime(createdAt) : ""}
                                            </Text>
                                            <Text className="text-sm text-content-secondary">
                                                {createdAt ? formatShortDate(createdAt, true) : ""}
                                            </Text>
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        <Text className="text-base">
                                            {clazzEntry
                                                ? `${clazzEntry.clazz.displayName} • ${clazzEntry.runner.name}`
                                                : "—"}
                                        </Text>
                                    </TableCell>
                                    <TableCell>
                                        <Text className="text-base">{triggerText}</Text>
                                    </TableCell>
                                    <TableCell>
                                        <div className="flex items-center gap-2">
                                            {status.showSpinner && (
                                                <IconSpinner
                                                    size="sm"
                                                    className="h-4 w-4 animate-spin text-content-secondary"
                                                />
                                            )}
                                            {status.icon === "success" && (
                                                <IconCheck size="sm" className="h-4 w-4 text-content-success" />
                                            )}
                                            {status.icon === "error" && (
                                                <IconClose size="sm" className="text-content-danger h-4 w-4" />
                                            )}
                                            {status.icon === "cancelled" && (
                                                <IconStop size="sm" className="text-content-danger h-4 w-4" />
                                            )}
                                            <Text className={cn("text-base", status.textClass)}>{status.label}</Text>
                                            {dur && <Text className="text-sm text-content-secondary">{dur}</Text>}
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        <div className="flex justify-end">
                                            <Button
                                                size="sm"
                                                variant="secondary"
                                                data-tracking-id="open-prebuild-logs"
                                                disabled={true}
                                                onClick={() =>
                                                    pb.status?.logUrl &&
                                                    window.open(pb.status.logUrl, "_blank", "noopener,noreferrer")
                                                }
                                            >
                                                Logs
                                            </Button>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </div>
        );
    }

    return (
        <div data-testid="project-details-prebuilds-page" className="flex flex-col gap-3">
            {runningCount > 0 && <Banner variant="info" text={`${runningCount} prebuild${runningCount > 1 ? "s" : ""} ${runningCount > 1 ? "are" : "is"} currently running...`} />}
            {content}
        </div>
    );
};

function triggerToText(trigger?: PrebuildMetadata_PrebuildTrigger): string {
    switch (trigger) {
        case PrebuildMetadata_PrebuildTrigger.MANUAL:
            return "Manual";
        case PrebuildMetadata_PrebuildTrigger.SCHEDULED:
            return "Scheduled";
        default:
            return "—";
    }
}

function statusInfo(phase?: PrebuildPhase): {
    label: string;
    showSpinner: boolean;
    icon?: "success" | "error" | "cancelled";
    textClass?: string;
} {
    switch (phase) {
        case PrebuildPhase.PENDING:
        case PrebuildPhase.STARTING:
        case PrebuildPhase.RUNNING:
        case PrebuildPhase.STOPPING:
            return { label: "Running", showSpinner: true };
        case PrebuildPhase.SNAPSHOTTING:
            return { label: "Snapshotting", showSpinner: true };
        case PrebuildPhase.COMPLETED:
            return { label: "Succeeded", showSpinner: false, icon: "success", textClass: "text-content-success" };
        case PrebuildPhase.FAILED:
            return { label: "Failed", showSpinner: false, icon: "error", textClass: "text-content-danger" };
        case PrebuildPhase.CANCELLING:
            return { label: "Cancelling", showSpinner: true };
        case PrebuildPhase.CANCELLED:
            return { label: "Cancelled", showSpinner: false, icon: "cancelled", textClass: "text-content-danger" };
        case PrebuildPhase.DELETING:
        case PrebuildPhase.DELETED:
            return { label: "Deleted", showSpinner: false, textClass: "text-content-secondary" };
        default:
            return { label: "—", showSpinner: false };
    }
}
